import DropdownButton from '@/components/ui/Buttons/DropdownButton';
import DropdownSelect from '@/components/ui/DropdownSelect';
import { FieldErrors, FieldNames } from '@credit-tracker/util/constants';
import { Fragment, RefObject, useCallback, useMemo, useState } from 'react';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import Button from '@/components/ui/Buttons/Button';
import TextInput from '@/components/ui/Form/TextInput';
import Link from '@/components/ui/Link';
import Shimmer from '@/components/ui/Shimmer';
import {
  Certificate,
  CLEPeriod,
  ICertificateViewModel,
  ICreditClassData
} from '@/@types/client-api';
import { roundCredits } from '@credit-tracker/util/TrackerHelpers';
import Icon from '@/components/ui/Icon';
import useCreditFormHook from '@credit-tracker/hooks/useCreditFormHook';
import {
  handleErrors,
  validateCreditAmountInput
} from '@credit-tracker/util/creditFormValidationHelpers';
import MenuText from '../../Menu/MenuText';

export type CreditTypesFormProps = {
  onChange: (cert: ICertificateViewModel) => void;
  handleUpdateErrors: (errorsFound: FieldErrors) => void;
  errors?: FieldErrors;
  creditDetailsRef: RefObject<HTMLDivElement>;
  externalCertificate?: Certificate | null;
  isLoading?: boolean | null;
  isCarryover?: boolean;
  creditTypes?: ICreditClassData[];
  validationMessages: string[] | undefined;
  selectedPeriod?: CLEPeriod;
};

const CreditTypesForm = ({
  onChange,
  handleUpdateErrors,
  errors,
  creditDetailsRef,
  externalCertificate,
  isLoading,
  isCarryover,
  creditTypes,
  selectedPeriod
}: CreditTypesFormProps) => {
  const [showCreditTypeDisabledTooltip, setShowCreditTypeDisabledTooltip] = useState(false);
  const { getSelectedCreditTypeLabel, getTotalCredits, formattedAvailableCreditTypes } =
    useCreditFormHook({
      certificate: externalCertificate ?? {},
      selectedCertificatesCarryover: [],
      creditTypes,
      selectedPeriod
    });

  const creditTypeShimmer = useMemo(() => {
    return (
      <div className="flex flex-row gap-x-4">
        <Shimmer className="relative h-[70px] w-[300px] *:h-full" />
        <Shimmer className="relative h-[70px] w-[182px] *:h-full" />
      </div>
    );
  }, []);

  const processErrorChange = useCallback(
    (error: FieldErrors) => {
      handleUpdateErrors({ ...errors, ...error });
    },
    [errors, handleUpdateErrors]
  );

  const certificateCreditTypes = useMemo(() => {
    return externalCertificate?.creditTypes?.length
      ? externalCertificate.creditTypes
      : [{ creditIssued: 0 }];
  }, [externalCertificate?.creditTypes]);

  const getCreditTypeValue = useCallback(
    (key?: string | null) => {
      if (isCarryover && !formattedAvailableCreditTypes?.length) {
        const _type = certificateCreditTypes?.find(a => a.creditType_PK === key);

        if (_type) {
          return {
            label: _type.creditTypeDescription,
            value: _type.creditType_PK
          };
        }
      }
      return formattedAvailableCreditTypes?.find(a => a.value === key);
    },
    [certificateCreditTypes, formattedAvailableCreditTypes, isCarryover]
  );

  const processUpdateErrorTypes = useCallback(
    (errorTypes: FieldNames[], index: number) => {
      const res: FieldErrors = {};

      for (const e of errorTypes) {
        const existingErrors = errors?.[e];
        delete existingErrors?.elementKeys?.[index];
        const stillHasErrors = !!Object.keys(existingErrors?.elementKeys ?? {})?.length;
        res[e] = stillHasErrors
          ? {
              ...existingErrors,
              forceMatch: stillHasErrors,
              showError: stillHasErrors
            }
          : undefined;
      }

      processErrorChange(res);
    },
    [errors, processErrorChange]
  );

  return (
    <>
      <p className="text-2-medium mb-4 mt-5">Credit details</p>
      <div
        className="mb-8 flex flex-col gap-y-8"
        data-component="CreditTypesForm"
        ref={creditDetailsRef}
      >
        {isLoading
          ? creditTypeShimmer
          : !!certificateCreditTypes?.length &&
            certificateCreditTypes?.map((detail, i) => {
              const selectedType = getCreditTypeValue(detail.creditType_PK);
              return (
                <Fragment key={i}>
                  <div className="space-y-8">
                    <div className="relative size-full">
                      {!creditTypes ? (
                        <ComplexTooltip variant="light" isOpen={!!showCreditTypeDisabledTooltip}>
                          <ComplexTooltip.Trigger asChild className="size-full min-h-[70px]">
                            <DropdownButton
                              state="closed"
                              placeholder="Select a credit type"
                              eyebrow="Credit type *"
                              dropdownError={errors?.[FieldNames.CREDIT_TYPE_FORMAT]}
                              label={
                                selectedType?.label ? (
                                  <MenuText
                                    value={selectedType.label}
                                    valueClassName="line-clamp-2"
                                  />
                                ) : (
                                  ''
                                )
                              }
                              className={
                                !creditTypes && !errors?.[FieldNames.CREDIT_TYPE_FORMAT]
                                  ? '!border !border-gray !text-gray'
                                  : ''
                              }
                              value={selectedType?.value ?? ''}
                              onClick={() => {
                                setShowCreditTypeDisabledTooltip(true);
                                processErrorChange({
                                  [FieldNames.DATE_COMPLETED]: {
                                    message: 'Enter a valid date',
                                    showError: true
                                  }
                                });
                              }}
                              disabled={false}
                              required
                            />
                          </ComplexTooltip.Trigger>
                          <ComplexTooltip.Content
                            ariaLabel="Aria label"
                            side="top"
                            onClose={() => setShowCreditTypeDisabledTooltip(false)}
                            className="pointer-events-auto"
                            nestedInModal
                          >
                            <div>
                              <p className="text-2">
                                {isCarryover
                                  ? 'Please select the compliance period above before selecting a credit type.'
                                  : 'Please enter the date completed above before selecting a credit type.'}
                              </p>
                            </div>
                            <ComplexTooltip.Buttons>
                              <Button
                                label="OK"
                                color="outline-black"
                                onClick={() => setShowCreditTypeDisabledTooltip(false)}
                              />
                            </ComplexTooltip.Buttons>
                          </ComplexTooltip.Content>
                        </ComplexTooltip>
                      ) : (
                        <DropdownSelect
                          button={({ isOpen, value, dropdownSide, ...props }) => (
                            <DropdownButton
                              state={isOpen ? 'open' : 'closed'}
                              label={
                                selectedType?.label || value ? (
                                  <MenuText
                                    value={selectedType?.label ?? value}
                                    valueClassName="line-clamp-2"
                                  />
                                ) : (
                                  ''
                                )
                              }
                              disabled={!creditTypes}
                              dropdownError={
                                handleErrors(FieldNames.CREDIT_TYPE_FORMAT, errors ?? {}, {
                                  [i]: i
                                })?.[0]
                              }
                              dropdownSide={dropdownSide}
                              {...props}
                            />
                          )}
                          label="Credit type"
                          placeholder="Select a credit type"
                          options={formattedAvailableCreditTypes}
                          value={selectedType?.value}
                          onValueChange={value => {
                            const updatedCreditDetails = [...(certificateCreditTypes ?? [])];
                            if (value?.length > 0) {
                              updatedCreditDetails[i] = {
                                ...updatedCreditDetails[i],
                                creditTypeDescription: getSelectedCreditTypeLabel(value),
                                creditType_PK: value
                              };
                              onChange({
                                creditTypes: updatedCreditDetails
                              });
                            }
                            const fieldNames = [FieldNames.CREDIT_TYPE_FORMAT];
                            if (!errors?.[FieldNames.CREDIT_TYPE_EARNED]?.showError) {
                              fieldNames.push(FieldNames.CREDIT_TYPE_EARNED);
                            }
                            processUpdateErrorTypes(fieldNames, i);
                          }}
                          menuStyle="form-field"
                          required
                          showError={show =>
                            (certificateCreditTypes?.length ?? 0) <= 1 &&
                            processErrorChange({
                              [FieldNames.CREDIT_TYPE_FORMAT]: {
                                message: 'Select a credit type',
                                showError: show
                              }
                            })
                          }
                        />
                      )}
                    </div>
                    <TextInput
                      variant="container"
                      label="Credit(s) earned"
                      disabled={!creditTypes}
                      value={
                        (detail?.creditIssued ?? 0) > 0
                          ? roundCredits(detail.creditIssued!)?.toString()
                          : ''
                      }
                      onChange={value => {
                        if (!validateCreditAmountInput(value)) return;
                        onChange({
                          creditTypes: certificateCreditTypes?.map((creditType, index) =>
                            index === i
                              ? { ...creditType, creditIssued: roundCredits(value) }
                              : creditType
                          )
                        });
                        processUpdateErrorTypes([FieldNames.CREDIT_TYPE_EARNED], i);
                      }}
                      placeholder="0"
                      className="size-full"
                      type="number"
                      acceptLetters={false}
                      acceptNumbers={true}
                      maxLength={5}
                      required
                      errors={
                        errors &&
                        handleErrors(
                          FieldNames.CREDIT_TYPE_EARNED,
                          errors,
                          detail.creditType_PK ? { [i]: detail.creditType_PK } : {}
                        )
                      }
                      forceInvalid={errors?.[FieldNames.CREDIT_TYPE_EARNED]?.showError}
                    />
                  </div>
                  <div className="flex flex-col gap-y-4">
                    {((externalCertificate && certificateCreditTypes?.length) ?? 0) >= 2 && (
                      <Link
                        href="#"
                        text="Remove"
                        variant="text-link-2"
                        className="flex self-end"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          onChange({
                            creditTypes: [
                              ...(certificateCreditTypes ?? []).filter((_, index) => index !== i)
                            ]
                          });
                        }}
                      />
                    )}
                    {i === (certificateCreditTypes ?? []).length - 1 && (
                      <button
                        className="text-2 flex max-w-fit items-center whitespace-nowrap py-2 pl-0 font-normal text-black hover:text-red disabled:bg-white disabled:text-gray-dark"
                        onClick={() =>
                          onChange({
                            creditTypes: [
                              ...(certificateCreditTypes ?? []),
                              { creditTypeDescription: '', creditIssued: undefined }
                            ]
                          })
                        }
                        disabled={!creditTypes}
                      >
                        <Icon name="add" size="small" />
                        <span className="ml-1">Add another credit type</span>
                      </button>
                    )}
                  </div>
                </Fragment>
              );
            })}
        <div className="text-2 font-normal text-gray-dark">{`Total credits added: ${getTotalCredits()}`}</div>
      </div>
    </>
  );
};
export default CreditTypesForm;
